import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listProjects, deleteProject, getProject, createProject, updateProject, formatUpdated, PROJECT_STATUS } from '../data/projects';
import { userName } from '../devUser';
import ShareDialog from './ShareDialog';
import './ProjectsHome.css';

const SCOPE_TABS = [
  { id: 'all', label: 'כל הפרויקטים' },
  { id: 'mine', label: 'הפרויקטים שלי' },
  { id: 'shared', label: 'שיתופים' },
];

const CARD_ACTIONS = [
  { id: 'open', label: 'פתיחה', icon: 'fi-rr-folder-open' },
  { id: 'edit', label: 'עריכה', icon: 'fi-rr-edit' },
  { id: 'image', label: 'תמונת פרויקט', icon: 'fi-rr-picture' },
  { id: 'duplicate', label: 'שכפול', icon: 'fi-rr-copy' },
  { id: 'share', label: 'שיתוף', icon: 'fi-rr-share' },
  { id: 'delete', label: 'מחיקה', icon: 'fi-rr-trash', danger: true },
];

// Downscale a chosen project image to a bounded JPEG data URL for the thumbnail.
function resizeProjectImage(file, max = 640) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load failed')); };
    img.src = url;
  });
}

function StatusPill({ status }) {
  const s = PROJECT_STATUS[status];
  if (!s) return null;
  return <span className={`rp-status rp-status--${s.tone}`}>{s.label}</span>;
}

function CardMenu({ onAction }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="rp-cardmenu" ref={ref}>
      <button
        type="button"
        className="rp-cardmenu-btn"
        aria-label="פעולות"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <i className="fi fi-rr-menu-dots-vertical" />
      </button>
      {open && (
        <div className="rp-cardmenu-pop" role="menu">
          {CARD_ACTIONS.map((a) => (
            <button
              key={a.id}
              type="button"
              role="menuitem"
              className={`rp-cardmenu-item ${a.danger ? 'is-danger' : ''}`}
              onClick={(e) => { e.stopPropagation(); setOpen(false); onAction?.(a.id); }}
            >
              <i className={`fi ${a.icon}`} />
              <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCardSkeleton() {
  return (
    <div className="rp-card rp-skelcard" aria-hidden="true">
      <div className="rp-card-thumb"><div className="rp-skel rp-skel-fill" /></div>
      <div className="rp-card-body">
        <div className="rp-skel" style={{ width: '62%', height: 15 }} />
        <div className="rp-skel" style={{ width: '88%', height: 12 }} />
        <div className="rp-skel" style={{ width: 84, height: 22, borderRadius: 999 }} />
        <div className="rp-skel rp-skelcard-foot" style={{ width: '48%', height: 11 }} />
      </div>
    </div>
  );
}

function ProjectCard({ project, onOpen, onAction }) {
  const editor = project.updatedByName || userName(project.updatedBy);
  const updated = formatUpdated(project.updatedAt);
  const footLabel = editor ? `עודכן ע״י ${editor} · ${updated}` : `עודכן לאחרונה ${updated}`;
  return (
    <article className="rp-card" onClick={() => onOpen?.(project)}>
      <div className={`rp-card-thumb rp-thumb-${project.thumb || 1}`}>
        {project.thumbnail
          ? <img className="rp-card-thumb-img" src={project.thumbnail} alt="" loading="lazy" />
          : <i className="fi fi-rr-building" />}
      </div>
      <div className="rp-card-body">
        <div className="rp-card-titlerow">
          <h3 className="rp-card-title">{project.name}</h3>
          <CardMenu onAction={(id) => onAction?.(id, project)} />
        </div>
        <p className="rp-card-loc">
          <i className="fi fi-rr-marker" />
          <span>{project.address}</span>
        </p>
        <div className="rp-card-tags">
          <StatusPill status={project.status} />
          {project.shared && (
            <span className="rp-shared"><i className="fi fi-rr-users" /> משותף</span>
          )}
        </div>
        <div className="rp-card-foot">
          <i className="fi fi-rr-time-past" />
          <span>{footLabel}</span>
        </div>
      </div>
    </article>
  );
}

export default function ProjectsHome({ filter = 'all', onFilterChange, onNewProject, onEditProject }) {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState('');
  const [view, setView] = useState('grid');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(''); // label shown while opening/duplicating
  const [sharing, setSharing] = useState(null); // project being shared, or null
  const imageInputRef = useRef(null);
  const imageTargetRef = useRef(null); // project awaiting a picked image

  const reload = useCallback(() => {
    setLoading(true);
    return listProjects(filter)
      .then((rows) => { setProjects(rows); setLoadError(''); })
      .catch((e) => setLoadError(e.message || 'טעינת הפרויקטים נכשלה.'))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { reload(); }, [reload]);

  const openForEdit = async (project) => {
    setBusy('פותח פרויקט…');
    try {
      const loaded = await getProject(project.id);
      onEditProject?.(loaded);
    } catch (e) {
      setLoadError(e.message || 'פתיחת הפרויקט נכשלה.');
    } finally {
      setBusy('');
    }
  };

  // Set a representative project image from the card menu (spec 7.2).
  const pickProjectImage = (project) => { imageTargetRef.current = project; imageInputRef.current?.click(); };
  const onProjectImagePicked = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const project = imageTargetRef.current;
    imageTargetRef.current = null;
    if (!file || !project) return;
    if (!file.type.startsWith('image/')) { setLoadError('יש לבחור קובץ תמונה.'); return; }
    setBusy('מעדכן תמונת פרויקט…');
    try {
      const thumbnail = await resizeProjectImage(file);
      await updateProject(project.id, { thumbnail });
      await reload();
    } catch (err) {
      setLoadError(err.message || 'עדכון התמונה נכשל.');
    } finally {
      setBusy('');
    }
  };

  const handleAction = async (id, project) => {
    if (id === 'open' || id === 'edit') return openForEdit(project);
    if (id === 'image') return pickProjectImage(project);
    if (id === 'share') return setSharing(project);
    if (id === 'delete') {
      await deleteProject(project.id).catch(() => {});
      return reload();
    }
    if (id === 'duplicate') {
      setBusy('משכפל פרויקט…');
      try {
        const { project: p, property, brief, stakeholders, documents } = await getProject(project.id);
        await createProject({
          name: `${p.name} (עותק)`,
          analysisEditor: p.analysis_editor,
          status: 'draft',
          property: {
            locMode: property?.loc_mode, city: property?.city, street: property?.street,
            houseNo: property?.house_number, gush: property?.block, helka: property?.parcel,
            subHelka: property?.sub_parcel, addressText: property?.address_text,
          },
          brief: { text: brief?.brief_text || '' },
          stakeholders: (stakeholders || []).map((s) => ({
            role: s.role_in_project, company: s.company, contact: s.contact, phone: s.phone, email: s.email,
          })),
          documents: (documents || []).map((d) => ({ fileName: d.file_name, documentType: d.document_type, fileMeta: d.file_size })),
        });
        await reload();
      } catch (e) {
        setLoadError(e.message || 'שכפול הפרויקט נכשל.');
      } finally {
        setBusy('');
      }
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return projects;
    return projects.filter((p) => `${p.name} ${p.address}`.includes(q));
  }, [projects, query]);

  return (
    <div className="rp-home">
      <div className="rp-home-head">
        <h1 className="rp-home-title">פרויקטים</h1>
        <div className="rp-home-tools">
          <div className="rp-search">
            <i className="fi fi-rr-search" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="חיפוש פרויקט..."
              aria-label="חיפוש פרויקט"
            />
          </div>
          <div className="rp-viewtoggle" role="group" aria-label="תצוגה">
            <button
              type="button"
              className={view === 'grid' ? 'is-active' : ''}
              onClick={() => setView('grid')}
              aria-label="תצוגת רשת"
              aria-pressed={view === 'grid'}
            >
              <i className="fi fi-rr-apps" />
            </button>
            <button
              type="button"
              className={view === 'list' ? 'is-active' : ''}
              onClick={() => setView('list')}
              aria-label="תצוגת רשימה"
              aria-pressed={view === 'list'}
            >
              <i className="fi fi-rr-list" />
            </button>
          </div>
          {/* Top-level add — always reachable even with many projects, where the
              trailing "+ new project" card scrolls out of view. */}
          <button type="button" className="rp-newbtn" onClick={onNewProject}>
            <i className="fi fi-rr-plus" /> פרויקט חדש
          </button>
        </div>
      </div>

      <div className="rp-scopebar" role="tablist" aria-label="סינון פרויקטים">
        {SCOPE_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={filter === t.id}
            className={`rp-scopetab ${filter === t.id ? 'is-active' : ''}`}
            onClick={() => onFilterChange?.(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loadError && (
        <div className="rp-loaderror" role="alert">
          <i className="fi fi-rr-exclamation" /> {loadError}
        </div>
      )}

      <div
        className={`rp-grid ${view === 'list' ? 'is-list' : ''} ${loading ? 'is-loading' : ''}`}
        aria-busy={loading}
      >
        {loading && projects.length === 0 ? (
          Array.from({ length: 6 }).map((_, i) => <ProjectCardSkeleton key={`sk-${i}`} />)
        ) : (
          filtered.map((p) => (
            <ProjectCard
              key={p.id}
              project={p}
              onOpen={openForEdit}
              onAction={handleAction}
            />
          ))
        )}

        <button type="button" className="rp-newcard" onClick={onNewProject}>
          <span className="rp-newcard-plus"><i className="fi fi-rr-plus" /></span>
          <span className="rp-newcard-title">פרויקט חדש</span>
          <span className="rp-newcard-sub">הוסף פרויקט חדש לניתוח</span>
        </button>
      </div>

      {busy && (
        <div className="rp-busy" role="status" aria-live="polite">
          <div className="rp-busy-card"><span className="rp-spinner" /> {busy}</div>
        </div>
      )}

      {sharing && <ShareDialog project={sharing} onClose={() => setSharing(null)} />}

      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onProjectImagePicked}
      />
    </div>
  );
}
